import { supabase } from "@/lib/supabase";
import { generateInstallerInvoicePDF } from "@/utils/generateInstallerInvoicePDF";

/** Minimal, display-only projection of a jobs row — mirrors the edge function's summarizeJob(). */
function summarizePayoutJob(row: Record<string, unknown>): PayoutJobSummary {
  const metadata = (row.metadata ?? {}) as Record<string, unknown>;
  const customers = row.customers as { first_name?: string | null; last_name?: string | null } | null;
  const customerFromJoin = customers
    ? [customers.first_name, customers.last_name].filter(Boolean).join(" ").trim()
    : "";
  return {
    jobNumber: (row.job_number as string | null) ?? null,
    customerName:
      customerFromJoin ||
      (metadata.customerName as string | null) ||
      null,
    projectType: (metadata.projectType as string | null) ?? (row.job_type as string | null) ?? null,
    city: (row.city as string | null) ?? null,
    state: (row.state as string | null) ?? null,
    scheduledInstallDate: (row.scheduled_start_at as string | null) ?? null,
  };
}

export type InstallerPayoutStatus =
  | "pending_review"
  | "approved"
  | "processing"
  | "paid"
  | "disputed"
  | "held";

export interface InstallerPayoutRow {
  id: string;
  organization_id: string;
  opportunity_id: string | null;
  execution_id: string | null;
  app_job_id: string | null;
  payout_type: string;
  status: InstallerPayoutStatus;
  payout_amount: number;
  currency: string;
  reference: string | null;
  approved_at: string | null;
  paid_at: string | null;
  created_at: string;
  updated_at: string;
}

export type InstallerInvoiceSourceType = "installer_upload" | "auto_generated";
export type InstallerInvoiceStatus = "open" | "partially_paid" | "paid" | "void";

export interface InstallerInvoiceLineItem {
  description: string;
  amount: number;
}

export interface InstallerPayoutInvoiceRow {
  id: string;
  organization_id: string;
  installer_payout_id: string;
  execution_id: string | null;
  contractor_id: string | null;
  vendor_name: string;
  invoice_number: string;
  source_type: InstallerInvoiceSourceType;
  bill_date: string;
  due_date: string | null;
  base_contract_amount: number;
  change_order_amount: number;
  field_material_amount: number;
  incentive_amount: number;
  total: number;
  line_items: InstallerInvoiceLineItem[];
  status: InstallerInvoiceStatus;
  amount_paid: number;
  missing_receipt_count: number;
  file_path: string;
  notes: string | null;
  submitted_by: string | null;
  submitted_at: string;
  created_at: string;
  updated_at: string;
}

export type InstallerPayoutIncentiveType = "five_star" | "other";
export type InstallerPayoutIncentiveStatus = "pending" | "approved" | "rejected";

export interface InstallerPayoutIncentiveRow {
  id: string;
  installer_payout_id: string;
  execution_id: string | null;
  type: InstallerPayoutIncentiveType;
  label: string | null;
  amount: number;
  status: InstallerPayoutIncentiveStatus;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  approved_by: string | null;
  approved_at: string | null;
  updated_at: string;
}

export interface PayoutJobSummary {
  jobNumber: string | null;
  customerName: string | null;
  projectType: string | null;
  city: string | null;
  state: string | null;
  scheduledInstallDate: string | null;
}

export interface ReadyToPayItem {
  payout: InstallerPayoutRow;
  invoice: InstallerPayoutInvoiceRow | null;
  incentives: InstallerPayoutIncentiveRow[];
  jobDisplayRef: string;
  job: PayoutJobSummary | null;
}

export interface PaidReimbursementItem {
  payout: InstallerPayoutRow;
  invoice: InstallerPayoutInvoiceRow;
}

/**
 * "Ready to pay" = payout has cleared closeout approval (approved) or the
 * installer has already submitted their invoice (processing), and isn't
 * paid/disputed/held yet. Surfaced in ProjectCloseoutCockpit since koredor's
 * main app has no other reader of installer_payouts today — this table is
 * otherwise only written to (fire-and-forget) from closeout.service.ts.
 */
export const InstallerPayoutsService = {
  async listReadyToPay(): Promise<{ data: ReadyToPayItem[]; error: unknown }> {
    // Uses edge function (service role) to bypass RLS on installer_payouts and
    // installer_payout_invoices — direct anon-client reads are blocked when
    // current_user_organization_id() returns null for Koredor JWT sessions.
    const { data, error } = await supabase.functions.invoke("list-ready-to-pay", {
      method: "POST",
    });
    if (error) return { data: [], error };
    const payload = data as { ok: boolean; items: ReadyToPayItem[] } | null;
    return { data: payload?.items ?? [], error: null };
  },

  /**
   * Paid payouts carrying a live field-material reimbursement claim — feeds
   * InstallerReimbursementPostingBridge (the GL posting rule for reimbursed
   * field materials). Same service-role edge-function pattern as
   * listReadyToPay, since direct anon-client reads hit the same RLS gap.
   */
  async listPaidForGL(): Promise<{ data: PaidReimbursementItem[]; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("list-paid-payouts-for-gl", {
      method: "POST",
    });
    if (error) return { data: [], error };
    const payload = data as { ok: boolean; items: PaidReimbursementItem[] } | null;
    return { data: payload?.items ?? [], error: null };
  },

  /**
   * Single job's most recent payout + its live (non-void) invoice, if any —
   * used by JobOfferReviewWorkspace's "View Invoice" affordance. Unlike
   * listReadyToPay, not filtered to approved/processing status: a job whose
   * payout has already reached paid should still show its invoice here for
   * reference.
   */
  async getInvoiceForJob(jobId: string): Promise<{ data: ReadyToPayItem | null; error: unknown }> {
    const { data: payout, error } = await supabase
      .from("installer_payouts")
      .select("id, organization_id, opportunity_id, execution_id, app_job_id, payout_type, status, payout_amount, currency, reference, approved_at, paid_at, created_at, updated_at")
      .eq("execution_id", jobId)
      .order("updated_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    if (error) return { data: null, error };
    if (!payout) return { data: null, error: null };

    const payoutRow = payout as InstallerPayoutRow;
    const { data: invoice, error: invoiceError } = await supabase
      .from("installer_payout_invoices")
      .select("id, organization_id, installer_payout_id, execution_id, contractor_id, vendor_name, invoice_number, source_type, bill_date, due_date, base_contract_amount, change_order_amount, field_material_amount, incentive_amount, total, line_items, status, amount_paid, missing_receipt_count, file_path, notes, submitted_by, submitted_at, created_at, updated_at")
      .eq("installer_payout_id", payoutRow.id)
      .neq("status", "void")
      .maybeSingle();

    if (invoiceError) return { data: null, error: invoiceError };

    let job: PayoutJobSummary | null = null;
    if (payoutRow.execution_id) {
      const { data: jobRow } = await supabase
        .from("jobs")
        .select("job_number, job_type, city, state, scheduled_start_at, metadata, customers(first_name, last_name)")
        .eq("id", payoutRow.execution_id)
        .maybeSingle();
      if (jobRow) job = summarizePayoutJob(jobRow as Record<string, unknown>);
    }

    const { data: incentives } = await supabase
      .from("installer_payout_incentives")
      .select("id, installer_payout_id, execution_id, type, label, amount, status, notes, created_by, created_at, approved_by, approved_at, updated_at")
      .eq("installer_payout_id", payoutRow.id)
      .order("created_at", { ascending: true });

    return {
      data: {
        payout: payoutRow,
        invoice: (invoice as InstallerPayoutInvoiceRow | null) ?? null,
        incentives: (incentives as InstallerPayoutIncentiveRow[] | null) ?? [],
        jobDisplayRef: payoutRow.app_job_id || payoutRow.execution_id || payoutRow.id,
        job,
      },
      error: null,
    };
  },

  async getInvoiceDownloadUrl(filePath: string, expiresInSeconds = 300): Promise<{ url: string | null; error: unknown }> {
    const { data, error } = await supabase.storage
      .from("installer-invoices")
      .createSignedUrl(filePath, expiresInSeconds);

    if (error || !data?.signedUrl) {
      return { url: null, error: error ?? new Error("Failed to create signed URL.") };
    }
    return { url: data.signedUrl, error: null };
  },

  /**
   * Staff-initiated equivalent of the installer's own upload-url -> generate
   * PDF -> upload -> submit flow, for a payout whose installer hasn't
   * submitted an invoice yet. Reuses the exact same edge functions the
   * installer portal calls; installer-invoice-upload-url / -submit now also
   * accept a finance-adjacent staff caller alongside contractor access.
   */
  async sendInvoiceOnBehalf(item: ReadyToPayItem): Promise<{ ok: boolean; invoice: InstallerPayoutInvoiceRow | null; error: unknown }> {
    const payoutId = item.payout.id;

    const { data: uploadInfo, error: uploadErr } = await supabase.functions.invoke("installer-invoice-upload-url", {
      body: { payoutId, fileName: `${item.jobDisplayRef || payoutId}-invoice.pdf`, mimeType: "application/pdf" },
    });
    if (uploadErr) return { ok: false, invoice: null, error: uploadErr };
    const info = uploadInfo as {
      uploadUrl?: string;
      token?: string;
      path?: string;
      invoiceNumber?: string;
      vendorName?: string;
      computedAmount?: {
        ok?: boolean;
        baseAmount: number;
        changeOrderAmount: number;
        fieldMaterialAmount: number;
        incentiveAmount: number;
        totalAmount: number;
        currency?: string;
      };
    } | null;
    if (!info?.path || !info?.token || !info?.invoiceNumber || !info.computedAmount?.ok) {
      return { ok: false, invoice: null, error: new Error("invoice_upload_url_failed") };
    }

    const pdfBlob = generateInstallerInvoicePDF({
      invoiceNumber: info.invoiceNumber,
      vendorName: info.vendorName || "Installer",
      jobDisplayRef: item.jobDisplayRef,
      job: item.job,
      computed: info.computedAmount,
    });

    const { error: putErr } = await supabase.storage
      .from("installer-invoices")
      .uploadToSignedUrl(info.path, info.token, pdfBlob, { contentType: "application/pdf" });
    if (putErr) return { ok: false, invoice: null, error: putErr };

    const { data: submitData, error: submitErr } = await supabase.functions.invoke("installer-invoice-submit", {
      body: { payoutId, filePath: info.path, invoiceNumber: info.invoiceNumber, mode: "auto_generated" },
    });
    if (submitErr) return { ok: false, invoice: null, error: submitErr };
    const payload = submitData as { ok?: boolean; invoice?: InstallerPayoutInvoiceRow } | null;
    if (!payload?.ok) return { ok: false, invoice: null, error: new Error("invoice_submit_failed") };
    return { ok: true, invoice: payload.invoice ?? null, error: null };
  },

  async markPaid(payoutId: string): Promise<{ ok: boolean; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("payout-mark-paid", {
      body: { payoutId },
    });
    if (error) return { ok: false, error };
    return { ok: Boolean((data as { ok?: boolean } | null)?.ok), error: null };
  },

  /** Voids a wrong/disputed invoice and reverts its payout to approved so the installer can resubmit. */
  async voidInvoice(invoiceId: string, reason?: string): Promise<{ ok: boolean; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("installer-invoice-void", {
      body: { invoiceId, reason },
    });
    if (error) return { ok: false, error };
    return { ok: Boolean((data as { ok?: boolean } | null)?.ok), error: null };
  },

  async createIncentive(input: {
    payoutId: string;
    type: InstallerPayoutIncentiveType;
    label?: string;
    amount: number;
    notes?: string;
  }): Promise<{ data: InstallerPayoutIncentiveRow | null; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("installer-payout-incentive-upsert", {
      body: { action: "create", ...input },
    });
    if (error) return { data: null, error };
    return { data: (data as { incentive?: InstallerPayoutIncentiveRow } | null)?.incentive ?? null, error: null };
  },

  async updateIncentive(input: {
    incentiveId: string;
    type?: InstallerPayoutIncentiveType;
    label?: string;
    amount?: number;
    notes?: string;
  }): Promise<{ data: InstallerPayoutIncentiveRow | null; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("installer-payout-incentive-upsert", {
      body: { action: "update", ...input },
    });
    if (error) return { data: null, error };
    return { data: (data as { incentive?: InstallerPayoutIncentiveRow } | null)?.incentive ?? null, error: null };
  },

  async approveIncentive(incentiveId: string): Promise<{ data: InstallerPayoutIncentiveRow | null; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("installer-payout-incentive-upsert", {
      body: { action: "approve", incentiveId },
    });
    if (error) return { data: null, error };
    return { data: (data as { incentive?: InstallerPayoutIncentiveRow } | null)?.incentive ?? null, error: null };
  },

  async rejectIncentive(incentiveId: string): Promise<{ data: InstallerPayoutIncentiveRow | null; error: unknown }> {
    const { data, error } = await supabase.functions.invoke("installer-payout-incentive-upsert", {
      body: { action: "reject", incentiveId },
    });
    if (error) return { data: null, error };
    return { data: (data as { incentive?: InstallerPayoutIncentiveRow } | null)?.incentive ?? null, error: null };
  },
};
